import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Kanban } from 'lucide-react';
import api from '../../services/api';
import Badge from '../../components/shared/Badge';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const ORIGEM_LABELS = {
  meta_ads: 'Meta Ads', google_ads: 'Google Ads', chaves_na_mao: 'Chaves na Mão',
  site: 'Site', indicacao: 'Indicação', ligacao: 'Ligação', whatsapp: 'WhatsApp',
  visita_espontanea: 'Visita', olx: 'OLX', sub100: 'Sub100', outro: 'Outro',
};

const ESTAGIOS = ['novo', 'contato_feito', 'visita_agendada', 'visita_realizada', 'proposta_enviada', 'proposta', 'negociacao', 'reservado', 'vendido', 'perdido', 'interesse', 'contrato'];

// Normaliza e formata telefone para exibição
function formatPhoneDisplay(phone) {
  if (!phone) return '—';
  const digits = String(phone).replace(/\D/g, '');
  if (digits.length === 10) {
    return `(${digits.slice(0,2)}) ${digits.slice(2,6)}-${digits.slice(6)}`;
  }
  if (digits.length === 11) {
    return `(${digits.slice(0,2)}) ${digits.slice(2,7)}-${digits.slice(7)}`;
  }
  return phone; // Retorna original se não conseguir formatar
}

export default function LeadsList() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [estagio, setEstagio] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['leads', estagio],
    queryFn: () => api.get('/leads', { params: { estagio: estagio || undefined, limit: 100 } }).then(r => r.data),
  });

  const leads = (data?.data || []).filter(l =>
    !search || l.nome?.toLowerCase().includes(search.toLowerCase()) ||
    l.telefone?.includes(search) || l.email?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-800">Leads</h1>
        <div className="flex gap-2">
          <button onClick={() => navigate('/leads/kanban')} className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50">
            <Kanban size={16} /> Kanban
          </button>
          <button onClick={() => navigate('/leads/kanban')} className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg text-sm hover:bg-secondary">
            <Plus size={16} /> Novo Lead
          </button>
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-xl shadow p-4 flex gap-4">
        <div className="flex-1 relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Buscar por nome, telefone ou email..."
            className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-secondary"
          />
        </div>
        <select value={estagio} onChange={e => setEstagio(e.target.value)} className="border border-gray-200 rounded-lg px-3 py-2 text-sm">
          <option value="">Todos os estágios</option>
          {ESTAGIOS.map(e => <option key={e} value={e}>{e.replace(/_/g, ' ')}</option>)}
        </select>
      </div>

      {/* Tabela */}
      <div className="bg-white rounded-xl shadow overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-gray-400">Carregando...</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-600 text-xs uppercase tracking-wider">
              <tr>
                <th className="px-6 py-3 text-left">Nome</th>
                <th className="px-6 py-3 text-left">Telefone</th>
                <th className="px-6 py-3 text-left">Origem</th>
                <th className="px-6 py-3 text-left">Interesse</th>
                <th className="px-6 py-3 text-left">Estágio</th>
                <th className="px-6 py-3 text-left">Temp.</th>
                <th className="px-6 py-3 text-left">Data</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {leads.map(lead => (
                <tr key={lead.id} onClick={() => navigate(`/leads/${lead.id}`)} className="hover:bg-gray-50 cursor-pointer">
                  <td className="px-6 py-4 font-medium text-gray-800">{lead.nome}</td>
                  <td className="px-6 py-4 text-gray-600">{formatPhoneDisplay(lead.telefone)}</td>
                  <td className="px-6 py-4"><Badge label={ORIGEM_LABELS[lead.origem] || lead.origem} variant={lead.origem} /></td>
                  <td className="px-6 py-4 text-gray-600 text-xs">{lead.empreendimento_nome || '—'}</td>
                  <td className="px-6 py-4 text-gray-600 text-xs capitalize">{lead.estagio_funil?.replace(/_/g, ' ')}</td>
                  <td className="px-6 py-4">
                    <span>{lead.temperatura === 'quente' ? '🔴' : lead.temperatura === 'morno' ? '🟡' : '🔵'}</span>
                  </td>
                  <td className="px-6 py-4 text-gray-400 text-xs">
                    {lead.created_at ? format(new Date(lead.created_at), 'dd/MM/yyyy', { locale: ptBR }) : '—'}
                  </td>
                </tr>
              ))}
              {!leads.length && (
                <tr><td colSpan={7} className="px-6 py-8 text-center text-gray-400">Nenhum lead encontrado</td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
